/* Boat Cover Finder — Manufacturer → Model → recommended covers with live,
   market-scoped pricing. Reads assets/boat-cover-finder-data.json (Manufacturer →
   Model → covers[{useCase, code, handle}]), fetches each product live by handle
   (Storefront API @inContext) and matches the exact variant by SKU so prices and
   variant ids are always correct for the current store/market. Initialises every
   .bcf instance on the page. No dependencies. */
(function () {
  "use strict";

  // After an add, open the theme's cart drawer (it rebuilds the header count and
  // drawer contents itself) by triggering the header cart icon's own handler.
  // Without a drawer (cart page mode), just refresh the count and contents.
  function refreshCart() {
    var opener = document.querySelector(".openCartDrawer");
    if (opener && window.jQuery) { opener.click(); return; }
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (window.buildCart && window.jQuery) { window.buildCart(cart, false); return; }
        document.querySelectorAll("[data-cart-count]").forEach(function (n) {
          n.textContent = cart.item_count < 100 ? cart.item_count : "";
          n.style.display = cart.item_count > 0 ? "" : "none";
        });
      })
      .catch(function () {});
  }

  function init(root) {
    if (root.dataset.bcfReady) return;
    root.dataset.bcfReady = "1";

    var market = window.OS_MARKET || {};
    var token = root.dataset.token || "";
    var country = String(market.country || root.dataset.country || "AU")
      .toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2) || "AU";
    var rootUrl = (market.root || root.dataset.rootUrl || "/").replace(/\/+$/, "") + "/";
    var apiVersion = root.dataset.apiVersion || "2024-10";
    var dataSrc = root.dataset.src;
    var enquireUrl = root.dataset.enquireUrl || "/pages/contact";
    var bestFit = root.dataset.badge || "Best Fit";
    var lang = String(root.dataset.locale || document.documentElement.lang || "en").toLowerCase().split("-")[0];

    var mfrSel = root.querySelector('[data-role="mfr"]');
    var mdlSel = root.querySelector('[data-role="model"]');
    var results = root.querySelector('[data-role="results"]');
    var emptyNote = root.querySelector('[data-role="empty"]');

    var currentRec = null;

    // Liquid's `t` filter HTML-escapes every translation whose key doesn't end
    // in _html, so an apostrophe arrives here as &#39;. These strings are then
    // written to the DOM as text, where nothing decodes them and the entity
    // shows up verbatim in the copy. Undo the escaping the filter applies.
    var ENTITIES = { "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"', "&#39;": "'", "&#x27;": "'" };
    function decodeEntities(value) {
      if (typeof value === "string") {
        return value.replace(/&(?:amp|lt|gt|quot|#39|#x27);/gi, function (m) {
          return ENTITIES[m.toLowerCase()] || m;
        });
      }
      if (value && typeof value === "object") {
        Object.keys(value).forEach(function (k) { value[k] = decodeEntities(value[k]); });
      }
      return value;
    }
    var DESCRIPTIONS = {};
    var descEl = root.querySelector('[data-role="descriptions"]');
    if (descEl) { try { DESCRIPTIONS = decodeEntities(JSON.parse(descEl.textContent) || {}); } catch (e) { DESCRIPTIONS = {}; } }

    // i18n: all visible chrome strings come from the section's i18n config (translatable)
    var I18N = {};
    var i18nEl = root.querySelector('[data-role="i18n"]');
    if (i18nEl) { try { I18N = decodeEntities(JSON.parse(i18nEl.textContent) || {}); } catch (e) { I18N = {}; } }
    function t(k, fb) { var v = I18N[k]; return (v != null && v !== "") ? v : fb; }

    var DATA = null;
    var productCache = {}; // handle -> Promise<product|null>

    function apiUrl() { return "/api/" + apiVersion + "/graphql.json"; }

    // Same format as the rest of the site (shop.money_format via Shopify.formatMoney,
    // e.g. "€256,41"); otherwise the page language, never the visitor's browser locale.
    function formatPrice(money) {
      if (!money) return "";
      try {
        var active = window.Shopify && Shopify.currency && Shopify.currency.active;
        if (window.Shopify && Shopify.formatMoney && typeof moneyFormat === "string" &&
            (!active || active === money.currencyCode)) {
          return Shopify.formatMoney(Math.round(parseFloat(money.amount) * 100), moneyFormat);
        }
      } catch (e) {}
      try {
        return new Intl.NumberFormat(lang, {
          style: "currency", currency: money.currencyCode,
        }).format(parseFloat(money.amount));
      } catch (e) {
        return money.currencyCode + " " + money.amount;
      }
    }

    function numericId(gid) { return String(gid || "").split("/").pop(); }

    function productUrl(handle, varId) {
      var u = rootUrl + "products/" + handle;
      return varId ? u + "?variant=" + varId : u;
    }

    function fetchProduct(handle) {
      if (productCache[handle]) return productCache[handle];
      var query =
        "query @inContext(country: " + country + ") { product(handle: " +
        JSON.stringify(handle) + ") { title featuredImage { url(transform:{maxWidth:640}) altText } " +
        "variants(first: 100) { nodes { sku title availableForSale id " +
        "price { amount currencyCode } image { url(transform:{maxWidth:640}) altText } " +
        "selectedOptions { name value } } } } }";
      var p = fetch(apiUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": token },
        body: JSON.stringify({ query: query }),
      })
        .then(function (r) { return r.json(); })
        .then(function (j) { return (j && j.data && j.data.product) || null; })
        .catch(function () { return null; });
      productCache[handle] = p;
      return p;
    }

    // The team asked to drop the leading "Universal" from product titles in the finder (20 Oct 2026).
    function displayTitle(t) { return String(t || "").replace(/^\s*Universal\s+/i, ""); }

    // Per-style card copy from the team's product copy doc (28 Oct 2026): style from the cover code, fabric from
    // the prefix (MP = PVC, MA = polyester). Styles without copy keep the product title + generic description.
    var CARDS = {}, cardsEl = root.querySelector('[data-role="cards"]');
    if (cardsEl) { try { CARDS = decodeEntities(JSON.parse(cardsEl.textContent) || {}); } catch (e) { CARDS = {}; } }
    var STYLE_BY_CODE = {
      "200": "bowrider", "202": "open_boat", "200A": "day_cruiser_bowrider", "200B": "day_cruiser_bowrider", "201": "cabin_cruiser",
      "203": "runabout", "203A": "day_cruiser_runabout", "203B": "day_cruiser_runabout", "204": "centre_console",
      "205": "side_console", "503": "pilot_cruiser", "505": "t_top"
    };
    function usable(v) { return typeof v === "string" && v !== "" && v.indexOf("translation missing") < 0; }
    function cardCopy(cover) {
      var m = /^(MA|MP)\s*(\d{3}[AB]?)/i.exec(cover.code || "");
      if (!m) return null;
      var fabric = m[1].toUpperCase() === "MP" ? "pvc" : "polyester";
      var s = (CARDS.styles || {})[STYLE_BY_CODE[m[2].toUpperCase()]];
      if (!s || !usable(s.title) || !usable(s["desc_" + fabric])) return null;
      var ticks = [CARDS["tick_" + fabric + "_1"], CARDS["tick_" + fabric + "_2"], s["tick_" + fabric], CARDS["tick_" + fabric + "_4"]].filter(usable);
      return {
        eyebrow: CARDS["eyebrow_" + fabric],
        title: s.title + (usable(CARDS["fabric_" + fabric]) ? " — " + CARDS["fabric_" + fabric] : ""),
        desc: s["desc_" + fabric],
        ticks: ticks
      };
    }

    function el(tag, cls, text) {
      var e = document.createElement(tag);
      if (cls) e.className = cls;
      if (text != null) e.textContent = text;
      return e;
    }

    // US finder boats read "<model> – <set-up> (<years>)" with the set-up in English;
    // swap in the storefront language's set-up from DATA.setupI18n when there is one.
    function setupLabel(model) {
      var dict = (DATA.setupI18n || {})[lang];
      var m = dict && /^(.+? – )(.+?)( \([^)]*\))?$/.exec(model);
      if (!m || !dict[m[2]]) return model;
      return m[1] + dict[m[2]] + (m[3] || "");
    }

    // ---- population ----
    function populateManufacturers() {
      var names = Object.keys(DATA.manufacturers).sort();
      names.forEach(function (n) {
        var o = el("option", null, n);
        o.value = n;
        mfrSel.appendChild(o);
      });
    }

    function populateModels(mfr) {
      mdlSel.innerHTML = '<option value="">' + t("select_model", "Select model…") + '</option>';
      var recs = (DATA.manufacturers[mfr] || []).slice().sort(function (a, b) {
        return a.model.localeCompare(b.model, undefined, { numeric: true });
      });
      recs.forEach(function (rec, i) {
        var label = setupLabel(rec.model);
        if (rec.length) label += "  (" + rec.length + "m)";
        var o = el("option", null, label);
        o.value = String(i);
        mdlSel.appendChild(o);
      });
      mdlSel.disabled = recs.length === 0;
      mdlSel._recs = recs;
    }

    // ---- rendering ----
    function orderedCovers(rec) {
      var order = DATA.useCaseOrder || [];
      return rec.covers.slice().sort(function (a, b) {
        var ia = order.indexOf(a.useCase), ib = order.indexOf(b.useCase);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      });
    }

    var EYEBROW = I18N.eyebrows || {
      "Standard cover": "Recommended",
      "Heavy Duty Winter": "Winter / heavy-duty",
    };

    function colorHex(name) {
      var n = (name || "").toLowerCase();
      if (n.indexOf("black") >= 0) return "#1c1f24";
      if (n.indexOf("grey") >= 0 || n.indexOf("gray") >= 0) return "#9aa3ad";
      if (n.indexOf("blue") >= 0) return "#1f4368";
      if (n.indexOf("white") >= 0) return "#eef0f2";
      if (n.indexOf("green") >= 0) return "#3a5a40";
      return "#8a93a3";
    }
    function isColorOpt(o) { return /colou?r/i.test(o.name); }
    function nonColorSig(v) {
      var so = v.selectedOptions || [], parts = [], i;
      for (i = 0; i < so.length; i++) { if (!isColorOpt(so[i])) parts.push(so[i].name + "=" + so[i].value); }
      return parts.sort().join("|");
    }
    function colorSiblings(variants, matched) {
      var mo = matched.selectedOptions || [], hasColor = false, i;
      for (i = 0; i < mo.length; i++) { if (isColorOpt(mo[i])) hasColor = true; }
      if (!hasColor) return null;
      var target = nonColorSig(matched), out = [];
      for (i = 0; i < variants.length; i++) {
        if (nonColorSig(variants[i]) !== target) continue;
        var cv = "", so = variants[i].selectedOptions || [], j;
        for (j = 0; j < so.length; j++) { if (isColorOpt(so[j])) cv = so[j].value; }
        if (cv) out.push({ name: cv, variant: variants[i] });
      }
      return out;
    }
    function fitLabel(variant, fallbackCode) {
      var so = variant.selectedOptions || [], parts = [], i;
      for (i = 0; i < so.length; i++) { if (!isColorOpt(so[i])) parts.push(so[i].value); }
      return parts.length ? (t("fits", "Fits boats") + " " + parts.join(" · ")) : ("Size " + fallbackCode);
    }

    function buildCard(cover, isBest) {
      var card = el("div", "bcf__card bcf__card--loading");
      var media = el("div", "bcf__card-media");
      var badgeEl = null;
      if (isBest) {
        badgeEl = el("span", "bcf__bestfit");
        badgeEl.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
        badgeEl.appendChild(document.createTextNode(bestFit));
        media.appendChild(badgeEl);
      }

      var copy = cardCopy(cover);
      var body = el("div", "bcf__card-body");
      var eyebrow = el("p", "bcf__eyebrow", (copy && usable(copy.eyebrow)) ? copy.eyebrow : (EYEBROW[cover.useCase] || cover.useCase));
      var title = el("h3", "bcf__card-title", copy ? copy.title : (displayTitle(cover.title) || "Boat cover"));
      var desc = el("p", "bcf__card-desc", copy ? copy.desc : (DESCRIPTIONS[cover.useCase] || ""));
      var ticks = null;
      if (copy && copy.ticks.length) {
        ticks = el("ul", "bcf__ticks");
        copy.ticks.forEach(function (tk) { ticks.appendChild(el("li", null, tk)); });
      }
      var sizeEl = el("p", "bcf__size", "");
      var colorsWrap = el("div", "bcf__colors");
      var price = el("p", "bcf__price", "");
      var actions = el("div", "bcf__actions");
      var view = el("a", "bcf__cta", t("view_cover", "View cover"));
      view.setAttribute("href", "#");
      actions.appendChild(view);

      body.appendChild(eyebrow);
      body.appendChild(title);
      if (desc.textContent) body.appendChild(desc);
      if (ticks) body.appendChild(ticks);
      body.appendChild(sizeEl);
      body.appendChild(colorsWrap);
      body.appendChild(price);
      body.appendChild(actions);
      card.appendChild(media);
      card.appendChild(body);

      if (cover.unresolved || !cover.handle) {
        renderEnquire(card, badgeEl, title, sizeEl, colorsWrap, price, view, cover);
        return card;
      }

      fetchProduct(cover.handle).then(function (product) {
        card.classList.remove("bcf__card--loading");
        if (!product) { // network/API failure: degrade to a plain PDP link
          if (badgeEl && badgeEl.parentNode) badgeEl.parentNode.removeChild(badgeEl);
          colorsWrap.remove(); price.remove(); sizeEl.remove();
          view.setAttribute("href", productUrl(cover.handle, null));
          return;
        }
        title.textContent = copy ? copy.title : (displayTitle(product.title || cover.title) || "Boat cover");
        var variants = (product.variants && product.variants.nodes) || [];
        var matched = null, i;
        for (i = 0; i < variants.length; i++) {
          if (variants[i].sku && variants[i].sku.trim().toUpperCase() === cover.code.toUpperCase()) { matched = variants[i]; break; }
        }
        if (!matched) { // listed product but this size not found: enquire
          renderEnquire(card, badgeEl, title, sizeEl, colorsWrap, price, view, cover);
          return;
        }
        var current = matched, addBtn = null;

        function paint(variant) {
          current = variant;
          var imgs = media.querySelectorAll("img"), k;
          for (k = imgs.length - 1; k >= 0; k--) { imgs[k].parentNode.removeChild(imgs[k]); }
          var img = variant.image || product.featuredImage;
          if (img && img.url) {
            var im = new Image(); im.loading = "lazy"; im.src = img.url;
            im.alt = title.textContent; im.className = "bcf__card-img";
            media.insertBefore(im, media.firstChild);
          }
          sizeEl.textContent = fitLabel(variant, cover.code);
          if (variant.availableForSale) {
            price.textContent = formatPrice(variant.price);
            price.className = "bcf__price";
            if (addBtn) { addBtn.disabled = false; addBtn.textContent = t("add_to_cart", "Add to cart"); }
          } else {
            price.textContent = t("unavailable", "Currently unavailable");
            price.className = "bcf__price bcf__price--soldout";
            if (addBtn) addBtn.disabled = true;
          }
          view.setAttribute("href", productUrl(cover.handle, numericId(variant.id)));
        }

        // colour swatches (only when a colour option exists)
        var sibs = colorSiblings(variants, matched);
        if (sibs && sibs.length > 1) {
          colorsWrap.appendChild(el("span", "bcf__colors-label", t("colour", "Colour")));
          var row = el("div", "bcf__swatches");
          var swEls = [];
          for (i = 0; i < sibs.length; i++) {
            (function (sib) {
              var b = el("button", "bcf__swatch");
              b.type = "button";
              b.setAttribute("aria-label", sib.name);
              b.title = sib.name;
              var dot = el("span", "bcf__swatch-dot");
              dot.style.background = colorHex(sib.name);
              b.appendChild(dot);
              b.appendChild(el("span", null, sib.name));
              b.addEventListener("click", function () {
                for (var e = 0; e < swEls.length; e++) { swEls[e].classList.remove("is-selected"); }
                b.classList.add("is-selected");
                paint(sib.variant);
              });
              if (sib.variant.sku === matched.sku) b.classList.add("is-selected");
              swEls.push(b);
              row.appendChild(b);
            })(sibs[i]);
          }
          colorsWrap.appendChild(row);
        } else {
          colorsWrap.remove();
        }

        addBtn = el("button", "bcf__cta bcf__cta--ghost", t("add_to_cart", "Add to cart"));
        addBtn.type = "button";
        addBtn.addEventListener("click", function () {
          addBtn.disabled = true; addBtn.textContent = "Adding…";
          fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: parseInt(numericId(current.id), 10), quantity: 1 }),
          })
            .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
            .then(function () { addBtn.textContent = "Added ✓"; refreshCart(); document.dispatchEvent(new CustomEvent("bcf:added")); setTimeout(function () { addBtn.disabled = false; }, 60); })
            .catch(function () { addBtn.textContent = t("add_to_cart", "Add to cart"); addBtn.disabled = false; });
        });
        actions.appendChild(addBtn);

        paint(matched);
      });
      return card;
    }

    function renderEnquire(card, badgeEl, title, sizeEl, colorsWrap, price, view, cover) {
      card.classList.remove("bcf__card--loading");
      card.classList.add("bcf__card--enquire");
      if (badgeEl && badgeEl.parentNode) badgeEl.parentNode.removeChild(badgeEl);
      if (colorsWrap && colorsWrap.parentNode) colorsWrap.remove();
      var copy = cardCopy(cover);
      title.textContent = cover.useCase === "Custom cover" ? "Custom-made cover" : (copy ? copy.title : (displayTitle(cover.title) || "Boat cover"));
      sizeEl.textContent = "Made to order — code " + cover.code;
      price.remove();
      view.textContent = "Enquire";
      view.className = "bcf__cta bcf__cta--ghost";
      view.setAttribute("href", enquireUrl);
    }

    function resolveCoverPrice(cover) {
      if (!cover || cover.unresolved || !cover.handle) return Promise.resolve(null);
      return fetchProduct(cover.handle).then(function (product) {
        if (!product) return null;
        var v = (product.variants && product.variants.nodes || []).filter(function (x) {
          return x.sku && x.sku.trim().toUpperCase() === cover.code.toUpperCase();
        })[0];
        return v && v.price ? parseFloat(v.price.amount) : null;
      }).catch(function () { return null; });
    }

    // show every cover, ordered most-expensive → least-expensive. No Best Fit badge: both covers fit the same
    // boat and PVC sells more in the EU, so the team dropped it (Joe, 28 Oct 2026).
    function renderResults(rec) {
      var covers = rec ? orderedCovers(rec) : [];
      results.innerHTML = "";
      emptyNote.hidden = true;
      if (!covers.length) { emptyNote.hidden = false; return; }
      Promise.all(covers.map(function (c) {
        return resolveCoverPrice(c).then(function (p) { return { cover: c, price: (p == null ? -1 : p) }; });
      })).then(function (items) {
        items.sort(function (a, b) { return b.price - a.price; });
        results.innerHTML = "";
        items.forEach(function (it) { results.appendChild(buildCard(it.cover, false)); });
      });
    }

    function resetDownstream() {
      results.innerHTML = "";
      emptyNote.hidden = true;
      currentRec = null;
    }

    // ---- wire up ----
    mfrSel.addEventListener("change", function () {
      resetDownstream();
      if (!mfrSel.value) { mdlSel.innerHTML = '<option value="">' + t("select_model", "Select model…") + '</option>'; mdlSel.disabled = true; return; }
      populateModels(mfrSel.value);
    });

    mdlSel.addEventListener("change", function () {
      resetDownstream();
      if (!mdlSel.value || !mdlSel._recs) return;
      currentRec = mdlSel._recs[parseInt(mdlSel.value, 10)];
      renderResults(currentRec);
    });

    if (!dataSrc) return;
    fetch(dataSrc)
      .then(function (r) { return r.json(); })
      .then(function (json) { DATA = json; populateManufacturers(); })
      .catch(function () {
        emptyNote.hidden = false;
        emptyNote.textContent = "Sorry — the boat cover finder could not load. Please refresh or contact us.";
      });
  }

  function boot() {
    document.querySelectorAll(".bcf").forEach(init);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
